/* eslint-disable no-console */

import { SeverityNumber } from "@opentelemetry/api-logs";
import { OTLPLogExporter } from "@opentelemetry/exporter-logs-otlp-http";
import { resourceFromAttributes } from "@opentelemetry/resources";
import { LoggerProvider, SimpleLogRecordProcessor } from "@opentelemetry/sdk-logs";
import { ATTR_SERVICE_NAME } from "@opentelemetry/semantic-conventions";
import { injectOidcSessionAttributes } from "shared/stores/oidc";
import { createLazy } from "shared/utils/fns";
import { fromError, ZodError } from "zod-validation-error";

const otelExporter = new OTLPLogExporter({
    url: "https://otel.bryx.com/http/v1/logs",
    httpAgentOptions: {},
    concurrencyLimit: 1,
});

const provider = createLazy(() => {
    const resource = resourceFromAttributes({
        [ATTR_SERVICE_NAME]: "emns-web",
        ["deployment.environment"]: import.meta.env.MODE,
        ...injectOidcSessionAttributes(),
    });

    return new LoggerProvider({ resource, forceFlushTimeoutMillis: 1000 });
});

const emitter = createLazy(() => {
    provider().addLogRecordProcessor(new SimpleLogRecordProcessor(otelExporter));
    return provider().getLogger("default");
});

export const logger = {
    warnZod: (error: ZodError, ...optionalParams: unknown[]) => {
        logger.warn(fromError(error).toString(), ...optionalParams);
    },
    log: (message: unknown, ...optionalParams: unknown[]) => {
        console.log(message, ...optionalParams);
        emitter().emit({
            severityNumber: SeverityNumber.INFO,
            severityText: "INFO",
            body: JSON.stringify([message].concat(optionalParams).join(" ")),
        });
    },

    warn: (message: unknown, ...optionalParams: unknown[]) => {
        console.warn(message, ...optionalParams);
        emitter().emit({
            severityNumber: SeverityNumber.WARN,
            severityText: "WARN",
            body: JSON.stringify([message].concat(optionalParams).join(" ")),
        });
    },

    error: (message: unknown, ...optionalParams: unknown[]): void => {
        console.error(message, ...optionalParams);
        emitter().emit({
            severityNumber: SeverityNumber.ERROR,
            severityText: "ERROR",
            body: JSON.stringify([message].concat(optionalParams.map((it) => JSON.stringify(it))).join("\n")),
        });

        provider().forceFlush().catch(console.error);
    },

    debug: (message: unknown, ...optionalParams: unknown[]) => {
        console.debug(message, ...optionalParams);
        emitter().emit({
            severityNumber: SeverityNumber.DEBUG,
            severityText: "DEBUG",
            body: JSON.stringify([message].concat(optionalParams).join(" ")),
        });
    },

    alert: (message: unknown) => {
        window.alert(message);
    },
};
